/* ============================================================
   Dathent — Edit Post screen
   Form view opened from Library / Calendar / Post Preview.
   4 tabs (Copy / Media / Schedule / Advanced) + sticky live mini-preview.
   Source: design pack pages/edit-post.jsx.
   ============================================================ */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons, BrandTints } from '../components/pack-icons/brands';
import { useActivity } from '../components/AppShell';

const PLATFORM_FROM_NAME = (raw) => {
  if (!raw) return null;
  const v = String(raw).toLowerCase();
  if (v.includes('insta')) return 'instagram';
  if (v.includes('face')) return 'facebook';
  if (v.includes('linke')) return 'linkedin';
  if (v === 'x' || v.includes('twit')) return 'x';
  if (v.includes('tik')) return 'tiktok';
  if (v.includes('thread')) return 'threads';
  if (v.includes('tele')) return 'telegram';
  if (v.includes('you')) return 'youtube';
  return v;
};

const PLATFORM_DEFS = [
  { id: 'instagram', name: 'Instagram', charLimit: 2200 },
  { id: 'facebook', name: 'Facebook', charLimit: 63206 },
  { id: 'linkedin', name: 'LinkedIn', charLimit: 3000 },
  { id: 'x', name: 'X', charLimit: 280 },
  { id: 'tiktok', name: 'TikTok', charLimit: 2200 },
  { id: 'telegram', name: 'Telegram', charLimit: 4096 },
  { id: 'threads', name: 'Threads', charLimit: 500 },
];

function cleanPromptPrefix(s) {
  if (typeof s !== 'string') return s;
  return s.replace(/^Generating(?:\s+\d+\s*\/\s*\d+)?\s*:\s*/i, '').trim();
}

function fmtScheduled(s) {
  if (!s) return 'later';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return 'later';
  return d.toLocaleString('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Format a Date or ISO string for <input type="datetime-local"> (YYYY-MM-DDTHH:mm in local time)
function toLocalDatetimeInput(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function errMessage(err) {
  if (!err) return 'Unknown error';
  const msg = err?.body?.error?.message || err?.body?.message || err?.data?.message || err?.message || '';
  const status = err?.status || err?.body?.error?.status;
  // Compose "<HTTP status> <message>" so we don't surface a generic
  // "Unknown error" when the API returns a useful error body.
  if (status && msg) return `${status}: ${msg}`;
  if (msg) return msg;
  return 'Unknown error';
}

/* ---------------- Field wrapper ---------------- */
function EpField({ label, hint, error, count, max, children }) {
  return (
    <div className={`ep-field ${error ? 'is-error' : ''}`}>
      <div className="ep-field-h">
        <label className="ep-field-l">{label}</label>
        {(count != null && max != null) && (
          <span className={`ep-field-count mono ${count > max ? 'is-over' : count > max * 0.85 ? 'is-warn' : ''}`}>
            {count.toLocaleString()} / {max.toLocaleString()}
          </span>
        )}
      </div>
      {children}
      {error && <div className="ep-field-err"><I.alert size={11} /> {error}</div>}
      {hint && !error && <div className="ep-field-hint">{hint}</div>}
    </div>
  );
}

/* ---------------- Slide thumb ----------------
 * Now draggable. We use native HTML5 drag-and-drop (no react-dnd dep)
 * so the tile can be picked up by the cover area and dropped on any
 * other slide. dataTransfer carries the source index; onDrop in the
 * receiving thumb calls onReorder(from, to) on the parent. Keyboard
 * arrows (chevLeft/chevRight) stay for a11y + non-mouse users. */
function SlideThumb({
  slide, idx, isActive, onClick, total, onMoveLeft, onMoveRight, onReorder, isDragging,
  onArmDelete, onCancelDelete, onConfirmDelete, isConfirmingDelete, isDeleting, canDelete
}) {
  const tint = slide.imageUrl
    ? { backgroundImage: `url("${slide.imageUrl}")` }
    : { background: `linear-gradient(135deg, ${slide.grad?.[0] || '#888'}, ${slide.grad?.[1] || '#444'})` };
  return (
    <div
      className={`ep-slide ${isActive ? 'is-active' : ''} ${isDragging ? 'is-dragging' : ''}`}
      onClick={onClick}
      draggable={!isConfirmingDelete}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(idx));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(e) => {
        e.preventDefault();
        const from = Number(e.dataTransfer.getData('text/plain'));
        if (Number.isFinite(from) && from !== idx && onReorder) onReorder(from, idx);
      }}
      title={isConfirmingDelete ? '' : 'Drag to reorder, or use arrows'}
    >
      <div className="ep-slide-cover" style={{
        ...tint,
        // 'contain' (was 'cover') so the user sees the FULL uploaded
        // image — no center-crop. Dark backdrop fills the letterbox
        // bars so empty space reads as intentional, not broken.
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: slide.imageUrl ? '#0f172a' : undefined
      }}>
        <span className="ep-slide-tag mono">{String(idx + 1).padStart(2, '0')}</span>
        {slide.cap && <span className="ep-slide-cap">{slide.cap.split('\n')[0]}</span>}
      </div>
      <div className="ep-slide-actions" onClick={(e) => e.stopPropagation()}>
        {idx > 0 && (
          <button title="Move left" onClick={onMoveLeft} type="button"><I.chevLeft size={11} /></button>
        )}
        {idx < total - 1 && (
          <button title="Move right" onClick={onMoveRight} type="button"><I.chevRight size={11} /></button>
        )}
        {/* Delete button — hidden when this is the last remaining slide
            on an image-required post (backend would refuse anyway, but
            hiding avoids a confusing error). Two-step confirm: first
            click arms the inline "Delete?" prompt overlay; second click
            within 4s fires the API. */}
        {canDelete && !isConfirmingDelete && (
          <button
            title="Delete slide"
            onClick={(e) => { e.stopPropagation(); onArmDelete(); }}
            type="button"
            style={{ color: 'var(--text-subtle)' }}
          >
            <I.trash size={11} />
          </button>
        )}
      </div>

      {/* Inline confirm overlay — full-tile dark scrim with Cancel +
          Delete buttons. Centered, no modal — UX stays on the same
          place, user doesn't lose context. */}
      {isConfirmingDelete && (
        <div
          className="ep-slide-confirm"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 8,
            background: 'rgba(15, 23, 42, 0.88)',
            backdropFilter: 'blur(2px)',
            borderRadius: 'var(--r-md)',
            zIndex: 5
          }}
        >
          <div style={{ fontSize: 12, color: '#fff', textAlign: 'center', padding: '0 12px' }}>
            Delete slide&nbsp;{idx + 1}?
          </div>
          <div style={{ display: 'inline-flex', gap: 6 }}>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={(e) => { e.stopPropagation(); onCancelDelete(); }}
              disabled={isDeleting}
              style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', borderColor: 'rgba(255,255,255,0.2)' }}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger btn-sm"
              type="button"
              onClick={(e) => { e.stopPropagation(); onConfirmDelete(); }}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Live mini preview ---------------- */
function MiniPreview({ platform, caption, hashtags, slide, slideIdx, slideCount, handle, onPrev, onNext, onOpen }) {
  const hasMultipleSlides = slideCount > 1;
  const slideBg = slide?.imageUrl
    ? {
        backgroundImage: `url("${slide.imageUrl}")`,
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#0f172a'
      }
    : { background: `linear-gradient(135deg, ${slide?.grad?.[0] || platform.tint?.from || '#888'}, ${slide?.grad?.[1] || platform.tint?.to || '#444'})` };
  // Click on media opens lightbox. Arrows stopPropagation so they
  // don't also fire onOpen — without that, clicking next/prev would
  // toggle the lightbox on every tap.
  const mediaClickable = Boolean(slide?.imageUrl && onOpen);
  const handleMediaClick = mediaClickable ? () => onOpen() : undefined;
  const handleMediaKeyDown = mediaClickable
    ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }
    : undefined;
  return (
    <div className="ep-mp">
      <div className="ep-mp-h">
        <span
          className={`ob-net-ic-${platform.id}`}
          style={{ width: 26, height: 26, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
        >
          {BrandIcons[platform.id]?.({ size: 13 })}
        </span>
        <div>
          <div className="ep-mp-name">{handle || platform.name}</div>
          <div className="ep-mp-sub mono">{platform.name}</div>
        </div>
      </div>
      <div
        className={`ep-mp-media${mediaClickable ? ' ep-mp-media--clickable' : ''}`}
        style={slideBg}
        onClick={handleMediaClick}
        onKeyDown={handleMediaKeyDown}
        role={mediaClickable ? 'button' : undefined}
        tabIndex={mediaClickable ? 0 : undefined}
        aria-label={mediaClickable ? 'Open full preview' : undefined}
      >
        <div className="ep-mp-grad" />
        {slide?.cap && (
          <div className="ep-mp-cap">
            {slide.cap.split('\n').map((line, i) => <div key={i}>{line}</div>)}
          </div>
        )}
        {hasMultipleSlides && onPrev && (
          <button
            type="button"
            className="ep-mp-nav ep-mp-nav--prev"
            onClick={(event) => {
              event.stopPropagation();
              onPrev();
            }}
            aria-label="Previous slide"
            disabled={slideIdx <= 0}
          >
            <I.chev_l size={14} />
          </button>
        )}
        {hasMultipleSlides && onNext && (
          <button
            type="button"
            className="ep-mp-nav ep-mp-nav--next"
            onClick={(event) => {
              event.stopPropagation();
              onNext();
            }}
            aria-label="Next slide"
            disabled={slideIdx >= slideCount - 1}
          >
            <I.chev_r size={14} />
          </button>
        )}
        {hasMultipleSlides && (
          <span className="ep-mp-counter mono">{slideIdx + 1} / {slideCount}</span>
        )}
      </div>
      <div className="ep-mp-cap-block">
        {handle && <span className="ep-mp-handle">{handle.replace('@', '').replace('/', '')}</span>}{' '}
        {caption.split('\n').slice(0, 3).map((p, i) => (
          <span key={i}>{p}{i < 2 && p && <br />}</span>
        ))}
        {hashtags.length > 0 && (
          <span className="ep-mp-tags"> {hashtags.slice(0, 4).join(' ')}{hashtags.length > 4 && '…'}</span>
        )}
      </div>
    </div>
  );
}

/* ---------------- Fullscreen lightbox ---------------- */
function SlideLightbox({ slides, slideIdx, onClose, onPrev, onNext }) {
  // Escape closes, arrow keys nav. We attach to window because the
  // lightbox content captures focus when opened but we don't want to
  // rely on a particular focused element to get keyboard nav.
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
      else if (event.key === 'ArrowLeft') onPrev();
      else if (event.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while the overlay is up — otherwise scrolling
    // the page behind the lightbox is jarring on touchpads.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext]);
  const slide = slides[slideIdx];
  const slideCount = slides.length;
  return (
    <div className="ep-lightbox" role="dialog" aria-modal="true" aria-label="Slide preview" onClick={onClose}>
      <div className="ep-lightbox-content" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="ep-lightbox-close" onClick={onClose} aria-label="Close preview">
          <I.x size={18} />
        </button>
        {slide?.imageUrl ? (
          <img
            src={slide.imageUrl}
            alt={slide?.cap || `Slide ${slideIdx + 1}`}
            className="ep-lightbox-img"
          />
        ) : (
          <div className="ep-lightbox-placeholder">No image for this slide</div>
        )}
        {slideCount > 1 && (
          <>
            <button
              type="button"
              className="ep-lightbox-nav ep-lightbox-nav--prev"
              onClick={onPrev}
              disabled={slideIdx <= 0}
              aria-label="Previous slide"
            >
              <I.chev_l size={22} />
            </button>
            <button
              type="button"
              className="ep-lightbox-nav ep-lightbox-nav--next"
              onClick={onNext}
              disabled={slideIdx >= slideCount - 1}
              aria-label="Next slide"
            >
              <I.chev_r size={22} />
            </button>
            <div className="ep-lightbox-counter mono">{slideIdx + 1} / {slideCount}</div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Main screen ---------------- */
export const EditPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setActivity } = useActivity();

  // Platform/target hint from the URL. Library and PostPreview append
  // ?platform=telegram so we can scope this Edit view to a single platform —
  // critical for batches that fan out across IG + Telegram + Threads, where
  // each content_item in Library represents one platform's variant but the
  // underlying generation job still carries all targets.
  const platformHint = useMemo(
    () => PLATFORM_FROM_NAME(searchParams.get('platform')) || null,
    [searchParams]
  );
  const targetIdHint = searchParams.get('target') || null;

  // Loaded post state
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [caption, setCaption] = useState('');
  const [hashtagsInput, setHashtagsInput] = useState('');
  // Alt text removed from UI per user request — was rarely useful and
  // confused the regenerate copy flow. Backend still accepts the field
  // on manual edit so older clients don't break.
  // Optional "what to change" prompt for text regeneration. Empty = ask
  // model to improve clarity (default behavior); non-empty = pass to
  // backend as additional instructions for the regen LLM call.
  const [regenNotes, setRegenNotes] = useState('');
  // Image-regen optional inputs (separate from regenNotes so the user
  // can keep both a text-tweak note and an image-tweak note around
  // without one stomping the other when they switch tabs).
  const [regenImageNotes, setRegenImageNotes] = useState('');
  const [regenImageLanguage, setRegenImageLanguage] = useState('');
  // "Customize" expander state — keeps the media tab clean by default,
  // shows the notes+language inputs only when the user clicks to reveal.
  const [regenImageCustomizeOpen, setRegenImageCustomizeOpen] = useState(false);
  // Whether to regenerate ONLY the currently selected slide (single) or
  // the entire carousel. Default = single when a carousel exists with
  // >1 slide; user can toggle to "all slides" if they want a full reset.
  const [regenImageScope, setRegenImageScope] = useState('single');
  // Local-only flags so the user sees something is happening while we
  // POST /regenerate or /regenerate-image. The activity-pill at the
  // top is already shown via setActivity, but a button-level spinner
  // is more obvious because it's right next to the trigger.
  const [regenTextBusy, setRegenTextBusy] = useState(false);
  const [regenImageBusy, setRegenImageBusy] = useState(false);
  const [uploadImageBusy, setUploadImageBusy] = useState(false);
  // Reorder autosave indicator — appears briefly after a successful
  // drag-drop persist so the user knows it stuck.
  const [reorderSavedAt, setReorderSavedAt] = useState(0);
  // Two-step delete confirm: holds the slide id currently in the
  // "click again to confirm" state. Cleared on success, on cancel,
  // or on a 4-second timeout if the user wanders off.
  const [confirmingDeleteSlideId, setConfirmingDeleteSlideId] = useState(null);
  const [deletingSlideId, setDeletingSlideId] = useState(null);
  // Fullscreen preview overlay on the right-side MiniPreview. Tied to
  // the same slideIdx state as Media tab, so flipping inside the
  // lightbox also moves the Media tab selection — single source of
  // truth across both panes (one of the user's explicit requests).
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // platforms used to hold platform IDs ("instagram", "threads"), which broke
  // multi-account workspaces — user couldn't pick which of two Telegrams to
  // publish to, and the auto-select inferred the wrong account at random.
  // Now stores accountIds; platform is derived from the account when needed.
  const [selectedAccounts, setSelectedAccounts] = useState(new Set());
  // All connected accounts for this user, grouped by platform — populated
  // once on mount so the schedule picker can render one row per account.
  const [allAccounts, setAllAccounts] = useState([]);
  const [slides, setSlides] = useState([]);
  const [slideIdx, setSlideIdx] = useState(0);
  const [scheduleMode, setScheduleMode] = useState('scheduled');
  const [scheduledAt, setScheduledAt] = useState('');
  // Initial tab honors ?tab= so deep-links from PostPreview ("Schedule this
  // post" / "Reschedule") land directly on the schedule tab.
  const initialTab = (() => {
    if (typeof window === 'undefined') return 'copy';
    const t = new URLSearchParams(window.location.search).get('tab');
    return ['copy', 'media', 'schedule'].includes(t) ? t : 'copy';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [saving, setSaving] = useState(false);

  // Capture originals to compute isDirty
  const [originals, setOriginals] = useState({ caption: '', hashtags: '', scheduledAt: '' });

  // Load connected social accounts once so the schedule picker can render
  // one row per account (and we can let the user re-target a different
  // account on the same network — common when two Telegrams or two IGs are
  // connected to one workspace).
  useEffect(() => {
    let cancelled = false;
    api.socialAccounts.list().then((res) => {
      if (cancelled) return;
      const list = res?.accounts || res?.data?.accounts || [];
      setAllAccounts(list.filter((a) => a.status === 'connected'));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Fetch post
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.content.get(id)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        setRaw(payload);
        // Hydrate form from server data
        const item = payload?.contentItem || payload;
        const v = payload?.activeVersion;
        const targets = payload?.publishSummary?.targets || [];
        const cap = cleanPromptPrefix(v?.body || v?.caption || item?.body || '');
        const hookOrHash = (v?.hashtags && Array.isArray(v.hashtags) ? v.hashtags : []).join(' ');
        setCaption(cap);
        setHashtagsInput(hookOrHash);
        // NOTE: account pre-selection lives in a separate effect below — it
        // needs allAccounts to resolve `platformHint` filtering (the URL hint
        // says "this is the Telegram variant", so we must drop the IG and
        // Threads peers from the originating job's target list).
        const repTarget = targets[0];
        const sched = repTarget?.scheduledAt || payload?.publishSummary?.lastScheduledAt || '';
        const schedLocal = toLocalDatetimeInput(sched);
        setScheduledAt(schedLocal);
        const slidesRaw = payload?.carouselSlides || [];
        const mediaUrls = payload?.mediaUrls || [];
        let mappedSlides;
        if (slidesRaw.length > 0) {
          mappedSlides = slidesRaw.map((s, i) => ({
            id: s.id || `s${i}`,
            kind: 'image',
            cap: s.caption || '',
            imageUrl: s.mediaAsset?.publicUrl || s.imageUrl || mediaUrls[i] || null,
            grad: ['#888', '#444'],
            serif: false,
            tag: `${String(i + 1).padStart(2, '0')} / ${String(slidesRaw.length).padStart(2, '0')}`,
          }));
        } else if (mediaUrls.length > 0) {
          mappedSlides = mediaUrls.map((url, i) => ({
            id: `s${i}`,
            kind: 'image',
            cap: '',
            imageUrl: url,
            grad: ['#888', '#444'],
            serif: false,
            tag: '',
          }));
        } else {
          mappedSlides = [{
            id: 's0', kind: 'image', cap: '',
            imageUrl: v?.imageUrl || payload?.mediaPreview?.url || null,
            grad: ['#888', '#444'], serif: false, tag: '',
          }];
        }
        setSlides(mappedSlides);
        setOriginals({ caption: cap, hashtags: hookOrHash, scheduledAt: schedLocal });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(errMessage(err) || 'Could not load post');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  // Init-once: pre-select originating accounts, scoped to `?platform=` hint.
  //
  // Source of truth (in priority order):
  //   1. publishSummary.targets — once the post has been scheduled at least
  //      once, these are the canonical "publish here" rows.
  //   2. job.targetAccountIds — the originating generation job's account list,
  //      used while the post is still a draft (publishSummary.targets is []).
  //
  // After selecting candidates we drop any account that doesn't match the
  // `platformHint`. Without this, a Library row that represents the Telegram
  // variant of a multi-platform batch would pre-select the IG + Threads
  // sibling accounts too, which is what the user reported.
  const [accountsInitialized, setAccountsInitialized] = useState(false);
  useEffect(() => {
    if (accountsInitialized) return;
    if (!raw || allAccounts.length === 0) return;
    const targets = raw?.publishSummary?.targets || [];
    const candidateIds = new Set();
    for (const t of targets) {
      if (t.socialAccountId) candidateIds.add(t.socialAccountId);
    }
    if (candidateIds.size === 0) {
      const jobTargets = Array.isArray(raw?.job?.targetAccountIds)
        ? raw.job.targetAccountIds
        : [];
      for (const accId of jobTargets) {
        if (accId) candidateIds.add(accId);
      }
    }
    // Apply platform hint filter: only keep accounts whose connected platform
    // matches the hint from the URL. If no hint, keep them all.
    const next = new Set();
    candidateIds.forEach((accId) => {
      const acc = allAccounts.find((a) => a.id === accId);
      if (!acc) return;
      if (platformHint && acc.platform !== platformHint) return;
      next.add(accId);
    });
    setSelectedAccounts(next);
    setAccountsInitialized(true);
  }, [raw, allAccounts, platformHint, accountsInitialized]);

  // Parsed hashtags from input
  const hashtags = useMemo(() => {
    return hashtagsInput
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => (t.startsWith('#') ? t : `#${t}`));
  }, [hashtagsInput]);

  const isDirty = useMemo(() => {
    return caption !== originals.caption
      || hashtagsInput !== originals.hashtags
      || scheduledAt !== originals.scheduledAt;
  }, [caption, hashtagsInput, scheduledAt, originals]);

  const toggleAccount = (accountId) => {
    setSelectedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  // Derived: distinct platforms across the picked accounts. Used by the
  // char-limit and preview-platform helpers below; selectedAccounts is
  // the canonical source of "what will publish" now.
  const selectedPlatforms = useMemo(() => {
    const set = new Set();
    selectedAccounts.forEach((accId) => {
      const acc = allAccounts.find((a) => a.id === accId);
      if (acc?.platform) set.add(acc.platform);
    });
    return set;
  }, [selectedAccounts, allAccounts]);

  const togglePlatform = (id) => {
    // Legacy: kept for the copy-tab platform selector. Toggles ALL accounts
    // for a platform on/off (caller doesn't care about per-account here).
    const accountsForPlatform = allAccounts.filter((a) => a.platform === id);
    setSelectedAccounts((prev) => {
      const next = new Set(prev);
      const anyOn = accountsForPlatform.some((a) => next.has(a.id));
      if (anyOn) {
        accountsForPlatform.forEach((a) => next.delete(a.id));
      } else {
        accountsForPlatform.forEach((a) => next.add(a.id));
      }
      return next;
    });
  };

  const charLimit = useMemo(() => {
    let min = Infinity;
    selectedPlatforms.forEach((id) => {
      const p = PLATFORM_DEFS.find((x) => x.id === id);
      if (p && p.charLimit < min) min = p.charLimit;
    });
    return Number.isFinite(min) ? min : 2200;
  }, [selectedPlatforms]);

  const currentPlatform = useMemo(() => {
    // Prefer the URL hint so the live preview stays on Telegram chrome even
    // if the user momentarily toggles all accounts off.
    const first = platformHint || Array.from(selectedPlatforms)[0] || 'instagram';
    const def = PLATFORM_DEFS.find((p) => p.id === first) || PLATFORM_DEFS[0];
    return { ...def, tint: BrandTints[def.id] };
  }, [selectedPlatforms, platformHint]);

  // Pick the handle from a target whose platform matches the hint (so the
  // preview shows "@dathentai_tg" not "@dathentai_ig" when viewing the
  // Telegram variant of a multi-platform batch).
  const handle = useMemo(() => {
    const targets = raw?.publishSummary?.targets || [];
    if (platformHint) {
      const matched = targets.find((t) => PLATFORM_FROM_NAME(t.platform) === platformHint);
      if (matched?.socialAccount?.displayName) return matched.socialAccount.displayName;
    }
    return targets[0]?.socialAccount?.displayName || '';
  }, [raw, platformHint]);

  // Whether the loaded post is already in draft state. When true, the
  // "Save as draft" radio is non-functional (just re-saves text edits, no
  // scheduling side effect) and confuses users into thinking it spawns a
  // second draft. We hide / disable it in that case.
  const isDraft = useMemo(() => {
    const item = raw?.contentItem || raw;
    const status = item?.status;
    return status === 'draft' || status === 'ready';
  }, [raw]);

  // If we landed on a draft and the user is on the Schedule tab, default to
  // "Schedule" mode rather than "Save as draft" so the picker actually does
  // something meaningful on Save.
  useEffect(() => {
    if (isDraft && scheduleMode === 'draft') {
      setScheduleMode('scheduled');
    }
    // intentionally not depending on scheduleMode so a user who switches
    // back to draft is not yanked away again. Only runs on isDraft flip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDraft]);

  const moveSlide = (idx, dir) => {
    const next = [...slides];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setSlides(next);
    setSlideIdx(target);
    // Autosave the new order — no Save button on media tab. If the post
    // hasn't been hydrated yet (slides are local-only "s0"/"s1" ids),
    // persistSlideOrder bails out internally.
    persistSlideOrder(next);
  };

  // Drag-and-drop reorder: pluck slide at `from`, splice it into `to`.
  // Active-slide index follows so the live preview stays on the slide
  // the user was looking at.
  const reorderSlide = (from, to) => {
    if (from === to) return;
    const next = [...slides];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setSlides(next);
    setSlideIdx((current) => {
      if (current === from) return to;
      if (from < current && current <= to) return current - 1;
      if (to <= current && current < from) return current + 1;
      return current;
    });
    persistSlideOrder(next);
  };

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    setActivity({ kind: 'planning', step: 'Saving changes…', project: 'Edit post' });
    try {
      let currentVersionId = raw?.activeVersion?.id;
      
      // 1. Save text changes via createManualEdit (creates new version)
      if (caption !== originals.caption || hashtagsInput !== originals.hashtags) {
        const editResult = await api.content.createManualEdit(id, {
          body: caption,
          caption,
          hashtags,
        });
        // Update version ID if a new version was created
        currentVersionId = editResult?.data?.activeVersion?.id || currentVersionId;
      }

      const scheduledPostId = raw?.publishSummary?.scheduledPostId;
      
      // 2. Create or update scheduled post (only if not draft mode)
      if ((scheduleMode === 'scheduled' || scheduleMode === 'now') && currentVersionId) {
        let localDate = scheduleMode === 'now' ? new Date() : new Date(scheduledAt);
        if (Number.isNaN(localDate.getTime()) && scheduleMode === 'scheduled') {
          throw new Error('Invalid date selected');
        }

        // Past-time safety net: if the user picked a time that's already in
        // the past (> 30 sec ago — small tolerance for typing/clock skew),
        // upgrade the request to publish_now. The cron scanner would publish
        // it anyway on its next tick, but this gets the user a faster path
        // AND makes the intent explicit on the API side ("publish_now" mode
        // instead of "manual" with a stale timestamp).
        let effectiveMode = scheduleMode;
        if (scheduleMode === 'scheduled' && localDate.getTime() < Date.now() - 30 * 1000) {
          effectiveMode = 'now';
          localDate = new Date();
        }

        // Build targets from the picked accounts. Each account selection
        // carries both platform and socialAccountId so multi-account
        // workspaces work correctly (two Telegrams stay distinct).
        const targets = Array.from(selectedAccounts)
          .map((accountId) => {
            const acc = allAccounts.find((a) => a.id === accountId);
            if (!acc) return null;
            return { platform: acc.platform, socialAccountId: acc.id };
          })
          .filter(Boolean);

        if (targets.length === 0) {
          throw new Error('Please select at least one account to publish to.');
        }

        if (scheduledPostId) {
          // Update existing scheduled post
          await api.scheduler.reschedule(scheduledPostId, { scheduledAt: localDate.toISOString() });
          await api.scheduler.changeTargets(scheduledPostId, { targets });
        } else {
          // Create new scheduled post
          await api.scheduler.create({
            contentItemId: id,
            contentVersionId: currentVersionId,
            scheduledAt: localDate.toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
            mode: effectiveMode === 'now' ? 'publish_now' : 'manual',
            targets,
          });
        }
      }

      setActivity({ kind: 'done', message: 'Post saved' });
      navigate(`/app/post/${encodeURIComponent(id)}`);
    } catch (err) {
      setActivity(null);
      console.error('Save error:', err);
      window.alert(`Save failed: ${errMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      await api.content.archive(id);
      navigate('/app/library');
    } catch (err) {
      window.alert(`Delete failed: ${errMessage(err)}`);
    }
  };

  const handleUploadCustomImage = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file || !id) return;
    // Reset the input so picking the same file twice in a row still triggers
    // a fresh upload.
    event.target.value = '';
    if (!file.type?.startsWith('image/')) {
      window.alert('Please pick an image file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      window.alert('Image is too large (max 10 MB).');
      return;
    }
    setUploadImageBusy(true);
    try {
      setActivity({
        kind: 'active',
        step: 'Uploading image',
        project: 'Edit post',
        returnTo: `/app/post/${encodeURIComponent(id)}/edit?tab=media`,
        returnLabel: 'Open media',
      });
      // Replace the currently-selected slide, not always slide 1. The
      // user clicked Upload while looking at slide N — that's where the
      // image should land.
      await api.content.uploadCustomImage(id, file, { slideIndex: slideIdx + 1 });
      setActivity({ kind: 'done', message: 'Image updated' });
      // Reload just the post (no full page refresh) so other state
      // — selected schedule, active tab, dirty edits — survives.
      const res = await api.content.get(id);
      const payload = res?.data && typeof res.data === 'object' ? res.data : res;
      if (payload) setRaw(payload);
    } catch (err) {
      setActivity(null);
      console.error('Upload error:', err);
      window.alert(`Upload failed: ${errMessage(err)}`);
    } finally {
      setUploadImageBusy(false);
    }
  };

  // Regenerate the TEXT (caption/hook/cta) using the post's existing
  // platform + language settings. If user typed something into the
  // regen-notes field, that's passed as additional instructions to the
  // LLM (e.g. "make it shorter and add a question at the end").
  const handleRegenerate = async () => {
    setRegenTextBusy(true);
    try {
      setActivity({ kind: 'active', step: 'Regenerating copy', project: 'Edit post' });
      const body = { confirmedCreditSpend: true };
      const trimmed = regenNotes.trim();
      if (trimmed) body.instructions = trimmed;
      const res = await api.content.regenerate(id, body);
      // Refresh local state immediately so user sees the new text. The
      // backend returns the new version inline.
      const payload = res?.data && typeof res.data === 'object' ? res.data : res;
      const v = payload?.activeVersion;
      if (v) {
        setCaption(cleanPromptPrefix(v.body || v.caption || ''));
        const tags = Array.isArray(v.hashtags) ? v.hashtags : [];
        setHashtagsInput(tags.join(' '));
        setOriginals((o) => ({
          ...o,
          caption: cleanPromptPrefix(v.body || v.caption || ''),
          hashtags: tags.join(' ')
        }));
      }
      setRaw((prev) => prev ? { ...prev, activeVersion: v ?? prev.activeVersion } : prev);
      setRegenNotes('');
      setActivity({ kind: 'done', message: 'Copy regenerated' });
    } catch (err) {
      setActivity(null);
      console.error('Regenerate error:', err);
      window.alert(`Regenerate failed: ${errMessage(err)}`);
    } finally {
      setRegenTextBusy(false);
    }
  };

  // Regenerate the image(s). When the customize panel is open the user
  // can pass:
  //   instructions — free-text note ("make it brighter", "add a person")
  //   language     — ISO override for image text (Lithuanian etc.)
  //   slideIndex   — set when scope='single' on a multi-slide carousel
  //                  → backend only swaps that one slide and keeps the
  //                  others; preserves the narrative role.
  const handleRegenerateImage = async () => {
    setRegenImageBusy(true);
    try {
      setActivity({
        kind: 'active',
        step: 'Regenerating image',
        project: 'Edit post',
        // returnTo lets the top activity pill bring the user BACK to this
        // exact screen + tab if they navigate away mid-regen. Without
        // this, the pill click had no batchId to fall back on (this
        // isn't a batch flow) and just toggled a panel.
        returnTo: `/app/post/${encodeURIComponent(id)}/edit?tab=media`,
        returnLabel: 'Open media',
      });
      const body = {};
      const trimmedNotes = regenImageNotes.trim();
      if (trimmedNotes) body.instructions = trimmedNotes;
      if (regenImageLanguage) body.language = regenImageLanguage;
      const carousel = Array.isArray(raw?.carouselSlides) ? raw.carouselSlides : [];
      if (regenImageScope === 'single' && carousel.length > 1 && slideIdx >= 0 && slideIdx < carousel.length) {
        // slide_index in D1 is 1-based.
        body.slideIndex = slideIdx + 1;
      }
      const res = await api.content.regenerateImage(id, body);
      const payload = res?.data && typeof res.data === 'object' ? res.data : res;
      if (payload) {
        setRaw(payload);
        // Rebuild the local slides array from the fresh carousel_slides.
        const slidesRaw = payload?.carouselSlides || [];
        if (slidesRaw.length > 0) {
          setSlides(slidesRaw.map((s, i) => ({
            id: s.id || `s${i}`,
            kind: 'image',
            cap: s.caption || '',
            imageUrl: s.mediaAsset?.publicUrl || s.imageUrl || null,
            grad: ['#888', '#444'],
            serif: false,
            tag: `${String(i + 1).padStart(2, '0')} / ${String(slidesRaw.length).padStart(2, '0')}`,
          })));
        }
      }
      setActivity({ kind: 'done', message: 'Image regenerated' });
      // Don't clear the notes — user often regenerates twice in a row
      // with the same hint, then tweaks. They can clear manually.
    } catch (err) {
      setActivity(null);
      console.error('Regenerate image error:', err);
      window.alert(`Regenerate image failed: ${errMessage(err)}`);
    } finally {
      setRegenImageBusy(false);
    }
  };

  // Persist current slide order to backend. Called after every reorder/
  // move gesture so the user doesn't lose their work — no Save button
  // needed on the media tab. Debounce-style guard: skip if no slide has
  // a real id yet (post still in initial gen) or the order matches what
  // raw.carouselSlides already has.
  const persistSlideOrder = async (orderedSlides) => {
    if (!id || !orderedSlides?.length) return;
    const carousel = Array.isArray(raw?.carouselSlides) ? raw.carouselSlides : [];
    if (!carousel.length) return; // No persisted slides to reorder
    const newIds = orderedSlides.map((s) => s.id).filter((sid) => sid && !String(sid).startsWith('s'));
    if (newIds.length !== carousel.length) return; // Mismatch — skip
    // Skip if order is already canonical (don't spam the API on no-op moves).
    const currentIds = carousel
      .slice()
      .sort((a, b) => Number(a.slideIndex || 0) - Number(b.slideIndex || 0))
      .map((s) => s.id);
    if (currentIds.every((cid, i) => cid === newIds[i])) return;
    try {
      await api.content.reorderSlides(id, newIds);
      // Update the local raw.carouselSlides to reflect the new order so
      // subsequent persists compute the right diff.
      setRaw((prev) => prev ? { ...prev, carouselSlides: newIds.map((nid, i) => {
        const existing = (prev.carouselSlides || []).find((cs) => cs.id === nid);
        return existing ? { ...existing, slideIndex: i + 1 } : null;
      }).filter(Boolean) } : prev);
      setReorderSavedAt(Date.now());
    } catch (err) {
      console.error('Reorder save failed:', err);
      // Soft-fail: the user sees the slides in the new order locally;
      // we don't roll back. Next refresh will pull canonical state.
    }
  };

  // Delete a single carousel slide. Two-step: first click arms the
  // confirm state (small inline "Delete?" prompt on the slide), second
  // click within 4s actually fires the API. Auto-cancels if the user
  // doesn't follow through — no accidental deletion.
  const armDeleteSlide = (slideId) => {
    setConfirmingDeleteSlideId(slideId);
    // Auto-clear the armed state after 4s so a stale "Delete?" prompt
    // doesn't sit there forever if the user wanders off.
    setTimeout(() => {
      setConfirmingDeleteSlideId((current) => current === slideId ? null : current);
    }, 4000);
  };

  const cancelDeleteSlide = () => setConfirmingDeleteSlideId(null);

  const confirmDeleteSlide = async (slideId) => {
    if (!id || !slideId) return;
    setDeletingSlideId(slideId);
    try {
      await api.content.deleteSlide(id, slideId);
      // Refresh post state from server so slide list + indices are
      // canonical (backend re-packs slide_index after delete).
      const res = await api.content.get(id);
      const payload = res?.data && typeof res.data === 'object' ? res.data : res;
      if (payload) {
        setRaw(payload);
        const slidesRaw = payload?.carouselSlides || [];
        if (slidesRaw.length > 0) {
          setSlides(slidesRaw.map((s, i) => ({
            id: s.id || `s${i}`,
            kind: 'image',
            cap: s.caption || '',
            imageUrl: s.mediaAsset?.publicUrl || s.imageUrl || null,
            grad: ['#888', '#444'],
            serif: false,
            tag: `${String(i + 1).padStart(2, '0')} / ${String(slidesRaw.length).padStart(2, '0')}`,
          })));
        }
        // Keep selection in-bounds.
        setSlideIdx((current) => Math.min(current, Math.max(0, slidesRaw.length - 1)));
      }
      setActivity({ kind: 'done', message: 'Slide deleted' });
    } catch (err) {
      setActivity(null);
      console.error('Delete slide error:', err);
      window.alert(`Could not delete slide: ${errMessage(err)}`);
    } finally {
      setDeletingSlideId(null);
      setConfirmingDeleteSlideId(null);
    }
  };

  if (loading) {
    return (
      <div className="ep-root">
        <div className="ep-top">
          <div>
            <Link to={id ? `/app/post/${encodeURIComponent(id)}` : '/app/library'} className="ep-back">
              <I.chevLeft size={14} /> Back
            </Link>
            <h1 className="ep-h">Loading…</h1>
          </div>
        </div>
      </div>
    );
  }
  if (error || !raw) {
    return (
      <div className="ep-root">
        <div className="ep-top">
          <div>
            <Link to="/app/library" className="ep-back"><I.chevLeft size={14} /> Library</Link>
            <h1 className="ep-h">Post not found</h1>
            <p className="ep-sub">{error || 'This post does not exist or you do not have access to it.'}</p>
          </div>
        </div>
      </div>
    );
  }

  const item = raw.contentItem;
  const itemStatus = item?.status || 'draft';

  return (
    <div className="ep-root">
      <div className="ep-top">
        <div>
          <Link to={`/app/post/${encodeURIComponent(id)}`} className="ep-back">
            <I.chevLeft size={14} /> Post preview
          </Link>
          <h1 className="ep-h">Edit post</h1>
          <p className="ep-sub">
            <span
              className={`ob-net-ic-${currentPlatform.id}`}
              style={{ width: 16, height: 16, borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', verticalAlign: 'middle' }}
            >
              {BrandIcons[currentPlatform.id]?.({ size: 10 })}
            </span>
            <span style={{ verticalAlign: 'middle', marginLeft: 6 }}>
              {currentPlatform.name} · {itemStatus}
            </span>
          </p>
        </div>
        {isDirty && (
          <span className="pill pill-warning" style={{ alignSelf: 'center' }}>
            <span className="dot" />Unsaved changes
          </span>
        )}
      </div>

      <div className="ep-grid">
        <div className="ep-form">
          <div className="ep-tabs">
            {[
              { id: 'copy', label: 'Copy', icon: I.edit },
              { id: 'media', label: 'Media', icon: I.image, count: slides.length },
              { id: 'schedule', label: 'Schedule', icon: I.calendar },
              { id: 'advanced', label: 'Advanced', icon: I.settings },
            ].map((t) => (
              <button
                key={t.id}
                className={`ep-tab ${activeTab === t.id ? 'is-active' : ''}`}
                onClick={() => setActiveTab(t.id)}
                type="button"
              >
                <t.icon size={13} />
                {t.label}
                {t.count != null && <span className="ep-tab-c mono">{t.count}</span>}
              </button>
            ))}
          </div>

          {activeTab === 'copy' && (
            <section className="card ep-card">
              <EpField
                label="Caption"
                count={caption.length}
                max={charLimit}
                hint={(() => {
                  // Compose hint with current platform char-limit + active
                  // publish language so user knows the language regen will
                  // produce. Language comes from the job snapshot (set per
                  // language-group at create time) — if user changed the
                  // social account's language since, regen picks up the
                  // CURRENT setting from social_accounts on the backend,
                  // not this cached label. We surface a "may change to X"
                  // hint when we detect a mismatch.
                  const itemLang = (raw?.job?.language || '').toLowerCase();
                  const accountLangs = Array.from(selectedAccounts)
                    .map((accId) => {
                      const acc = allAccounts.find((a) => a.id === accId);
                      return (acc?.publishLanguage || acc?.defaultPublishLanguage || '').toLowerCase();
                    })
                    .filter(Boolean);
                  const currentAccountLang = accountLangs[0] || itemLang;
                  const langLabel = currentAccountLang ? ` · 🌐 ${currentAccountLang.toUpperCase()}` : '';
                  const mismatch = itemLang && currentAccountLang && itemLang !== currentAccountLang
                    ? ` (was ${itemLang.toUpperCase()}; regenerate will use ${currentAccountLang.toUpperCase()})`
                    : '';
                  return `Most restrictive limit shown (${currentPlatform.name})${langLabel}${mismatch}.`;
                })()}
              >
                <textarea
                  className="ep-textarea"
                  rows={8}
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Write your post copy…"
                />
              </EpField>

              {/* Optional "what to change" textarea — empty = generic
                  "improve clarity" pass; non-empty = passed as user
                  instructions to the regen LLM. Helps users steer the
                  rewrite ("make it shorter", "add a question", "less
                  salesy", etc) without writing a full new caption. */}
              <EpField
                label="What to change (optional)"
                hint="Leave empty for a general polish pass. Otherwise tell the AI what you'd like different."
              >
                <textarea
                  className="ep-textarea"
                  rows={2}
                  value={regenNotes}
                  onChange={(e) => setRegenNotes(e.target.value)}
                  placeholder="e.g. shorter, add a question at the end, less salesy…"
                  disabled={regenTextBusy}
                />
              </EpField>

              <div className="ep-toolbar">
                <button
                  className="ep-toolbar-btn"
                  type="button"
                  onClick={handleRegenerate}
                  disabled={regenTextBusy}
                >
                  <I.sparkles size={12} />
                  {regenTextBusy ? 'Regenerating…' : 'Regenerate copy'}
                </button>
              </div>

              <EpField
                label="Hashtags"
                count={hashtags.length}
                max={30}
                hint="Space-separated. We auto-prepend # if missing."
              >
                <input
                  className="ep-input"
                  type="text"
                  value={hashtagsInput}
                  onChange={(e) => setHashtagsInput(e.target.value)}
                  placeholder="#example #another"
                />
                {hashtags.length > 0 && (
                  <div className="ep-hashtags">
                    {hashtags.map((t, i) => (
                      <span key={i} className="ep-hashtag">{t}</span>
                    ))}
                  </div>
                )}
              </EpField>
              {/* Alt text removed per user feedback — it rarely matched
                  the actual image, added noise to the form, and the
                  per-platform alt is now set on publish by the platform
                  worker (Instagram/Twitter alt_text API). */}
            </section>
          )}

          {activeTab === 'media' && (
            <section className="card ep-card">
              <EpField label="Slides" hint="Click to focus, drag to reorder, or use the arrows.">
                <div className="ep-slides">
                  {slides.map((s, i) => {
                    // Delete is hidden when the post is image-required
                    // and only 1 slide remains — backend would refuse.
                    // For other formats (text_post etc.) deletion is
                    // always available since the post can exist with
                    // zero slides.
                    const imageRequired = raw?.contentItem?.format === 'image_post'
                      || raw?.contentItem?.format === 'carousel';
                    const canDelete = !imageRequired || slides.length > 1;
                    return (
                      <SlideThumb
                        key={s.id}
                        slide={s}
                        idx={i}
                        total={slides.length}
                        isActive={i === slideIdx}
                        onClick={() => setSlideIdx(i)}
                        onMoveLeft={() => moveSlide(i, -1)}
                        onMoveRight={() => moveSlide(i, +1)}
                        onReorder={reorderSlide}
                        canDelete={canDelete}
                        isConfirmingDelete={confirmingDeleteSlideId === s.id}
                        isDeleting={deletingSlideId === s.id}
                        onArmDelete={() => armDeleteSlide(s.id)}
                        onCancelDelete={cancelDeleteSlide}
                        onConfirmDelete={() => confirmDeleteSlide(s.id)}
                      />
                    );
                  })}
                </div>
              </EpField>

              {/* Customize panel — collapsed by default so the media tab
                  stays clean. Opens to reveal a small notes textarea
                  + language dropdown + scope toggle (single slide vs
                  whole carousel). All three are optional — leave the
                  panel closed and Regenerate uses the post's current
                  settings as before. */}
              <details
                className="ep-regen-customize"
                open={regenImageCustomizeOpen}
                onToggle={(e) => setRegenImageCustomizeOpen(e.currentTarget.open)}
                style={{ marginBottom: 8 }}
              >
                <summary style={{
                  cursor: 'pointer', fontSize: 12.5, color: 'var(--text-muted)',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 0', userSelect: 'none'
                }}>
                  <I.sparkles size={11} />
                  Customize regeneration
                </summary>
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: 8,
                  marginTop: 8, padding: '10px 12px',
                  background: 'var(--bg-subtle)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--r-sm)'
                }}>
                  <div>
                    <label style={{ fontSize: 11.5, color: 'var(--text-subtle)', display: 'block', marginBottom: 4 }}>
                      What to change? (optional)
                    </label>
                    <textarea
                      value={regenImageNotes}
                      onChange={(e) => setRegenImageNotes(e.target.value)}
                      placeholder="e.g. softer lighting, add a person in frame, more product detail…"
                      rows={2}
                      style={{
                        width: '100%', fontSize: 13, padding: '6px 8px',
                        background: 'var(--bg-elev)', border: '1px solid var(--border)',
                        borderRadius: 'var(--r-sm)', color: 'var(--text)', resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 160px' }}>
                      <label style={{ fontSize: 11.5, color: 'var(--text-subtle)', display: 'block', marginBottom: 4 }}>
                        Image text language
                      </label>
                      <select
                        value={regenImageLanguage}
                        onChange={(e) => setRegenImageLanguage(e.target.value)}
                        style={{
                          width: '100%', fontSize: 13, padding: '6px 8px',
                          background: 'var(--bg-elev)', border: '1px solid var(--border)',
                          borderRadius: 'var(--r-sm)', color: 'var(--text)',
                          fontFamily: 'inherit'
                        }}
                      >
                        <option value="">Keep current language</option>
                        <option value="en">English</option>
                        <option value="uk">Українська</option>
                        <option value="ru">Русский</option>
                        <option value="de">Deutsch</option>
                        <option value="fr">Français</option>
                        <option value="es">Español</option>
                        <option value="pl">Polski</option>
                        <option value="cs">Čeština</option>
                        <option value="pt">Português</option>
                        <option value="nl">Nederlands</option>
                        <option value="lt">Lietuvių</option>
                        <option value="lv">Latviešu</option>
                      </select>
                    </div>
                    {slides.length > 1 && (
                      <div style={{ flex: '1 1 200px' }}>
                        <label style={{ fontSize: 11.5, color: 'var(--text-subtle)', display: 'block', marginBottom: 4 }}>
                          Scope
                        </label>
                        <select
                          value={regenImageScope}
                          onChange={(e) => setRegenImageScope(e.target.value)}
                          style={{
                            width: '100%', fontSize: 13, padding: '6px 8px',
                            background: 'var(--bg-elev)', border: '1px solid var(--border)',
                            borderRadius: 'var(--r-sm)', color: 'var(--text)',
                            fontFamily: 'inherit'
                          }}
                        >
                          <option value="single">Just slide {slideIdx + 1}</option>
                          <option value="all">All {slides.length} slides</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </details>

              <div className="ep-media-tools" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Distinct from copy-tab Regenerate: this only re-generates
                    the images, keeps caption/copy as-is. Hits the
                    /regenerate-image endpoint added in content-worker. */}
                <button
                  className="btn btn-ghost btn-sm"
                  type="button"
                  onClick={handleRegenerateImage}
                  disabled={regenImageBusy || uploadImageBusy}
                >
                  <I.sparkles size={12} />
                  {regenImageBusy ? 'Regenerating image…' : 'Regenerate image'}
                </button>
                <label className={`btn btn-ghost btn-sm ${uploadImageBusy || regenImageBusy ? 'is-disabled' : ''}`} style={{ cursor: uploadImageBusy || regenImageBusy ? 'not-allowed' : 'pointer', opacity: uploadImageBusy || regenImageBusy ? 0.55 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <I.image size={12} />
                  {uploadImageBusy ? 'Uploading…' : 'Upload custom image'}
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploadImageBusy || regenImageBusy}
                    style={{ display: 'none' }}
                    onChange={handleUploadCustomImage}
                  />
                </label>
              </div>
            </section>
          )}

          {activeTab === 'schedule' && (
            <section className="card ep-card">
              <EpField label="When to publish">
                <div className="ep-radio-group">
                  <button
                    className={`ep-radio ${scheduleMode === 'now' ? 'is-active' : ''}`}
                    onClick={() => setScheduleMode('now')}
                    type="button"
                  >
                    <span className="ep-radio-dot" />
                    <div>
                      <div className="ep-radio-l">Publish now</div>
                      <div className="ep-radio-s">Push to the platform as soon as you save.</div>
                    </div>
                  </button>
                  <button
                    className={`ep-radio ${scheduleMode === 'scheduled' ? 'is-active' : ''}`}
                    onClick={() => setScheduleMode('scheduled')}
                    type="button"
                  >
                    <span className="ep-radio-dot" />
                    <div>
                      <div className="ep-radio-l">Schedule</div>
                      <div className="ep-radio-s">Pick a date and time.</div>
                    </div>
                  </button>
                  <button
                    className={`ep-radio ${scheduleMode === 'draft' ? 'is-active' : ''} ${isDraft ? 'is-disabled' : ''}`}
                    onClick={() => {
                      if (isDraft) return; // already a draft — no-op
                      setScheduleMode('draft');
                    }}
                    disabled={isDraft}
                    type="button"
                    style={isDraft ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                  >
                    <span className="ep-radio-dot" />
                    <div>
                      <div className="ep-radio-l">
                        {isDraft ? 'Already a draft' : 'Save as draft'}
                      </div>
                      <div className="ep-radio-s">
                        {isDraft
                          ? 'This post is already saved as a draft. Pick Publish now or Schedule to send it.'
                          : "Don't schedule — keep editing later."}
                      </div>
                    </div>
                  </button>
                </div>
              </EpField>

              {scheduleMode === 'scheduled' && (
                <EpField label="Date & time" hint="Saved as your account timezone.">
                  {/* Quick-pick chips. Each one writes a value in the local
                      datetime-local format ("YYYY-MM-DDTHH:mm") so the
                      input below picks it up and the user can still
                      fine-tune. Removes the awkward "click 5 times in
                      a native picker" experience for the common cases. */}
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                    {[
                      { label: 'In 1 hour', mins: 60 },
                      { label: 'Tomorrow 9am', mins: null, hour: 9, dayOffset: 1 },
                      { label: 'Tomorrow 6pm', mins: null, hour: 18, dayOffset: 1 },
                      { label: 'Next Monday 9am', mins: null, hour: 9, nextDay: 1 },
                      { label: 'In 1 week', mins: 60 * 24 * 7 },
                    ].map((opt) => (
                      <button
                        key={opt.label}
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => {
                          const d = new Date();
                          if (opt.mins) {
                            d.setMinutes(d.getMinutes() + opt.mins);
                          } else if (opt.dayOffset != null) {
                            d.setDate(d.getDate() + opt.dayOffset);
                            d.setHours(opt.hour, 0, 0, 0);
                          } else if (opt.nextDay != null) {
                            const delta = (opt.nextDay - d.getDay() + 7) % 7 || 7;
                            d.setDate(d.getDate() + delta);
                            d.setHours(opt.hour, 0, 0, 0);
                          }
                          // Format as YYYY-MM-DDTHH:mm in LOCAL time — the
                          // datetime-local input expects local, not UTC.
                          const pad = (n) => String(n).padStart(2, '0');
                          const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                          setScheduledAt(local);
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <input
                    className="ep-input"
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                  />
                  {/* Past-time hint: if the user picks a moment that's already
                      passed, surface that on save we'll publish immediately
                      instead of silently dropping the post into the queue.
                      Mirrors the safety net in handleSave. */}
                  {scheduledAt && (() => {
                    const t = new Date(scheduledAt).getTime();
                    if (Number.isFinite(t) && t < Date.now() - 30 * 1000) {
                      return (
                        <div style={{
                          fontSize: 12,
                          color: 'var(--accent)',
                          marginTop: 6,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}>
                          <I.alert size={12} /> That time has already passed — Save will publish immediately.
                        </div>
                      );
                    }
                    return null;
                  })()}
                </EpField>
              )}

              <EpField label="Publish to" hint="Pick each connected account separately — two Telegrams = two rows.">
                <div className="ep-plats">
                  {/* Render one row per connected account so multi-account
                      workspaces (e.g. two Telegrams) can be picked
                      individually. Disabled rows show platforms with no
                      connected account so the user knows what's missing. */}
                  {allAccounts.length === 0 ? (
                    <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
                      Loading connected accounts…
                    </div>
                  ) : null}
                  {allAccounts.map((acc) => {
                    const p = PLATFORM_DEFS.find((x) => x.id === acc.platform);
                    if (!p) return null;
                    const on = selectedAccounts.has(acc.id);
                    const langChip = acc.publishLanguage || acc.defaultPublishLanguage;
                    return (
                      <button
                        key={acc.id}
                        className={`ep-plat ${on ? 'is-on' : ''}`}
                        onClick={() => toggleAccount(acc.id)}
                        type="button"
                      >
                        <span
                          className={`ob-net-ic-${p.id}`}
                          style={{ width: 26, height: 26, borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
                        >
                          {BrandIcons[p.id]?.({ size: 13 })}
                        </span>
                        <div className="ep-plat-text">
                          <div className="ep-plat-name" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span>{p.name}</span>
                            {langChip && (
                              <span style={{
                                fontSize: 9,
                                fontFamily: 'var(--font-mono)',
                                letterSpacing: '0.04em',
                                padding: '1px 5px',
                                background: 'var(--accent-soft)',
                                color: 'var(--accent)',
                                borderRadius: 3,
                                border: '1px solid var(--border-2)',
                              }}>
                                🌐 {String(langChip).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-subtle)' }}>
                            {acc.displayName || acc.externalAccountId || 'Connected'}
                          </div>
                        </div>
                        <span className={`ep-plat-toggle ${on ? 'is-on' : ''}`}>
                          {on && <I.check size={11} />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </EpField>
            </section>
          )}

          {activeTab === 'advanced' && (
            <section className="card ep-card">
              <div className="ep-danger">
                <div>
                  <div className="ep-danger-h">Delete this post</div>
                  <div className="ep-danger-s">Remove from your library and any scheduled slot. This can't be undone.</div>
                </div>
                <button className="btn btn-sm pp-danger" onClick={handleDelete} type="button">
                  <I.trash size={12} /> Delete post
                </button>
              </div>
            </section>
          )}
        </div>

        <aside className="ep-side">
          <div className="ep-side-sticky">
            <div className="ep-side-h">
              <div className="eyebrow"><span>Live preview</span></div>
              <h3>How it looks</h3>
            </div>
            <MiniPreview
              platform={currentPlatform}
              caption={caption}
              hashtags={hashtags}
              slide={slides[slideIdx] || slides[0]}
              slideIdx={slideIdx}
              slideCount={slides.length}
              handle={handle}
              onPrev={() => setSlideIdx((current) => Math.max(0, current - 1))}
              onNext={() => setSlideIdx((current) => Math.min(slides.length - 1, current + 1))}
              onOpen={() => setLightboxOpen(true)}
            />

            <div className="ep-actions card">
              {scheduleMode === 'now' ? (
                <button
                  className="btn btn-primary ep-cta"
                  onClick={handleSave}
                  disabled={saving}
                  type="button"
                >
                  <I.zap size={13} /> Save &amp; publish now
                </button>
              ) : scheduleMode === 'draft' ? (
                <button
                  className="btn btn-primary ep-cta"
                  onClick={handleSave}
                  disabled={saving || !isDirty}
                  type="button"
                >
                  <I.check size={13} /> Save draft
                </button>
              ) : (
                <button
                  className="btn btn-primary ep-cta"
                  onClick={handleSave}
                  disabled={saving || (!isDirty && !scheduledAt)}
                  type="button"
                >
                  <I.check size={13} /> Save · publish {fmtScheduled(scheduledAt)}
                </button>
              )}
              <div className="ep-actions-row">
                <Link className="btn btn-ghost btn-sm" to={`/app/post/${encodeURIComponent(id)}`}>Cancel</Link>
                <Link className="btn btn-ghost btn-sm" to={`/app/post/${encodeURIComponent(id)}`}>
                  <I.eye size={12} /> Preview
                </Link>
              </div>
            </div>
          </div>
        </aside>
      </div>
      {lightboxOpen && slides.length > 0 && (
        <SlideLightbox
          slides={slides}
          slideIdx={slideIdx}
          onClose={() => setLightboxOpen(false)}
          onPrev={() => setSlideIdx((current) => Math.max(0, current - 1))}
          onNext={() => setSlideIdx((current) => Math.min(slides.length - 1, current + 1))}
        />
      )}
    </div>
  );
};

export default EditPost;
